"use client";

import React, { useState, useEffect, useRef } from "react";
import { UploadIcon, SparkleAIIcon, AIVerificationIcon, PendingReviewIcon } from "./components/Icons";
import api from "../../api/api.js";
import { uploadImage, deleteImage } from "../../services/firebase";
import { logMessage } from "../../utils/helpers";
import { AiOutlineEdit } from "react-icons/ai";
import { BiEdit, BiLoaderAlt } from "react-icons/bi";
import { MdError } from "react-icons/md";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function ClaimProgressDialog({ isOpen, onClose, projectId, projectDetail, startLoading, stopLoading, pendingReviewRequests, fetchProjectDetail, initialMilestone = 0 }) {
  const [dialogState, setDialogState] = useState("upload"); // upload, verifying, success, failure
  const [selectedMilestone, setSelectedMilestone] = useState(0);
  const [userSelectedMilestone, setUserSelectedMilestone] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [uploadedImagePreview, setUploadedImagePreview] = useState("");
  const [verificationResult, setVerificationResult] = useState(null);
  const [showRequestReviewDialog, setShowRequestReviewDialog] = useState(false);
  const [showReviewPendingDialog, setShowReviewPendingDialog] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewSubmitError, setReviewSubmitError] = useState("");
  const [limitErrorMessage, setLimitErrorMessage] = useState("");
  const progressImageUrlRef = useRef("");
  const isLimitReached = Boolean(limitErrorMessage);

  const visibleMilestones = [25, 50, 75, 100];

  const resolveEffectiveMilestone = (aiVerifiedPercent) => {
    const percent = Math.min(100, Math.max(0, Number(aiVerifiedPercent) || 0));
    if (percent >= 100) return 100;

    let effective = 0;
    for (const milestone of visibleMilestones) {
      if (percent >= milestone) effective = milestone;
    }
    return effective;
  };

  const getRawAiPercent = (result, fallback = 0) =>
    Math.min(100, Math.max(0, Number(result?.ai_verified_completion_percent ?? fallback) || 0));

  const getDisplayMilestone = () =>
    userSelectedMilestone ||
    Number(verificationResult?.requested_milestone) ||
    selectedMilestone ||
    0;

  const openRequestReviewDialog = () => {
    setReviewSubmitError("");
    setShowRequestReviewDialog(true);
  };

  const applyAiMilestoneSelection = (data) => Number(data?.effective_milestone) || 0;

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setUploadedImageUrl("");
      setUploadedImagePreview("");
      setSelectedMilestone(0);
      setUserSelectedMilestone(0);
      setVerificationResult(null);
      setDialogState("upload");
      setIsClaiming(false);
      setIsVerifying(false);
      setIsSubmittingReview(false);
      setReviewSubmitError("");
      setLimitErrorMessage("");
      progressImageUrlRef.current = "";
      setShowRequestReviewDialog(false);
      setShowReviewPendingDialog(false);
      return;
    }

    if (initialMilestone > 0) {
      setSelectedMilestone(initialMilestone);
    }
  }, [isOpen, initialMilestone]);

  const showLimitToast = (message) => {
    toast.error(message, {
      toastId: "ai-verification-weekly-limit",
      style: { whiteSpace: "pre-line" },
    });
  };

  const applyLimitReached = (message) => {
    const text =
      message ||
      "You've used all 5 AI Progress Verifications available for this week.\nYour limit resets every Monday at 12:00 AM, after which you'll be able to use AI Progress Verification again.";
    setLimitErrorMessage(text);
    setDialogState("upload");
    showLimitToast(text);
  };

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const checkLimitOnOpen = async () => {
      try {
        const response = await api.checkAIVerificationLimit({
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        });
        if (cancelled || response.status === 1) return;
        applyLimitReached(response.message);
      } catch (error) {
        console.error("Error checking AI verification limit:", error);
      }
    };

    checkLimitOnOpen();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const getOriginalImage = () => {
    const projectImage = projectDetail?.image;
    if (Array.isArray(projectImage) && projectImage.length > 0) {
      return projectImage[0];
    }
    if (typeof projectImage === "string" && projectImage) {
      return projectImage;
    }
    return projectDetail?.canvas_start_image || "";
  };

  if (!isOpen) return null;

  const handleFileChange = (e) => {
    if (isLimitReached) {
      showLimitToast(limitErrorMessage);
      return;
    }

    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const maxSizeMB = 5;
      const maxSizeBytes = maxSizeMB * 1024 * 1024;

      if (file.size > maxSizeBytes) {
        logMessage(`Image size should not exceed ${maxSizeMB} MB.`, "alert");
        return;
      }

      setSelectedFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const AI_VERIFICATION_ERROR_MESSAGE =
    "We're having trouble verifying your progress with AI right now. Please try again in a few moments.";

  const getClientTimezone = () =>
    Intl.DateTimeFormat().resolvedOptions().timeZone || "";

  const checkWeeklyVerificationLimit = async () => {
    if (isLimitReached) {
      showLimitToast(limitErrorMessage);
      return false;
    }

    const response = await api.checkAIVerificationLimit({
      timezone: getClientTimezone(),
    });
    if (response.status !== 1) {
      applyLimitReached(response.message);
      return false;
    }
    return true;
  };

  const handleSubmitForReview = async () => {
    if (isLimitReached) {
      showLimitToast(limitErrorMessage);
      return;
    }

    if (isVerifying) return;

    if (!selectedFile || selectedMilestone === 0) {
    //   logMessage("Please upload an image and select a milestone", "alert");
    toast.warn("Please upload an image and select a milestone")
      return;
    }

    const originalImage = getOriginalImage();
    if (!originalImage) {
      toast.error("Project reference image is missing. Please add a project image first.");
      return;
    }

    try {
      setUserSelectedMilestone(selectedMilestone);
      setIsVerifying(true);
      setDialogState("verifying");

      const withinLimit = await checkWeeklyVerificationLimit();
      if (!withinLimit) return;

      const firebaseUrl = await uploadImage(selectedFile, "milestone");
      if (!firebaseUrl) {
        throw new Error("Failed to upload image to Firebase");
      }
      progressImageUrlRef.current = firebaseUrl;
      setUploadedImageUrl(firebaseUrl);

      const response = await api.projectAIVerification({
        project_id: projectId,
        original_image: originalImage,
        current_image: firebaseUrl,
        milestone_completed: selectedMilestone,
        timezone: getClientTimezone(),
      });

      if (response.status === 1) {
        setVerificationResult({ ...response.data, current_image: firebaseUrl });
        const effectiveMilestone = applyAiMilestoneSelection(response.data);
        setDialogState(
          response.data?.can_claim && effectiveMilestone >= 25 ? "success" : "failure"
        );
      } else if (response.message?.includes("AI Progress Verifications available for this week")) {
        applyLimitReached(response.message);
      } else {
        setVerificationResult({
          current_image: firebaseUrl,
          is_ai_error: true,
          requested_milestone: selectedMilestone,
          ai_verified_completion_percent: 0,
          effective_milestone: 0,
        });
        setDialogState("failure");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "AI verification failed";
      if (errorMessage.includes("Firebase") || errorMessage.includes("upload")) {
        logMessage(errorMessage, "alert");
        toast.error(errorMessage);
        setDialogState("upload");
      } else if (errorMessage.includes("AI Progress Verifications available for this week")) {
        applyLimitReached(errorMessage);
      } else {
        setVerificationResult({
          current_image: progressImageUrlRef.current || uploadedImageUrl,
          is_ai_error: true,
          requested_milestone: selectedMilestone,
          ai_verified_completion_percent: 0,
          effective_milestone: 0,
        });
        setDialogState("failure");
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleClaimPoints = async () => {
    if (isClaiming) return;

    try {
      setIsClaiming(true);
      const response = await api.claimMilestonePoints({
        project_id: projectId,
        milestone_completed: verificationResult.effective_milestone,
      });

      if (response.status === 1) {
        logMessage("Points claimed successfully!", "alert");
        toast.success("Points claimed successfully");
        await fetchProjectDetail();
        onClose();
      } else {
        logMessage(response.message, "alert");
        toast.error(response.message || "Failed to claim points");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || error?.message || "Failed to claim points";
      logMessage(errorMessage, "alert");
      toast.error(errorMessage);
    } finally {
      setIsClaiming(false);
    }
  };

  const handleUploadNewPhoto = async () => {
    // Delete previous uploaded image from Firebase
    if (uploadedImageUrl) {
      await deleteImage(uploadedImageUrl);
    }
    
    // Reset states
    setSelectedFile(null);
    setUploadedImageUrl("");
    setUploadedImagePreview("");
    progressImageUrlRef.current = "";
    setSelectedMilestone(0);
    setUserSelectedMilestone(0);
    setVerificationResult(null);
    setDialogState("upload");

    try {
      const response = await api.checkAIVerificationLimit({
        timezone: getClientTimezone(),
      });
      if (response.status !== 1) {
        applyLimitReached(response.message);
      } else {
        setLimitErrorMessage("");
      }
    } catch (error) {
      console.error("Error checking AI verification limit:", error);
    }
  };

  const handleClose = async () => {
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (dialogState === "verifying") return;
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const resolveProgressImageUrl = async () => {
    let imageUrl =
      progressImageUrlRef.current ||
      uploadedImageUrl ||
      verificationResult?.current_image ||
      "";

    if (!imageUrl && selectedFile) {
      imageUrl = await uploadImage(selectedFile, "milestone");
      if (imageUrl) {
        progressImageUrlRef.current = imageUrl;
        setUploadedImageUrl(imageUrl);
        setVerificationResult((prev) =>
          prev ? { ...prev, current_image: imageUrl } : prev
        );
      }
    }

    return imageUrl;
  };

  const showReviewSubmitted = () => {
    setReviewSubmitError("");
    setShowRequestReviewDialog(false);
    setShowReviewPendingDialog(true);
    fetchProjectDetail?.();
  };

  const handleRequestReviewSubmit = async () => {
    if (isSubmittingReview) return;
    setReviewSubmitError("");

    const originalImage = getOriginalImage();
    if (!originalImage) {
      const message = "Project reference image is missing.";
      setReviewSubmitError(message);
      logMessage(message, "alert");
      return;
    }

    if (getDisplayMilestone() < 25) {
      const message = "Please select a milestone of at least 25%.";
      setReviewSubmitError(message);
      return;
    }

    try {
      setIsSubmittingReview(true);

      const currentImage = await resolveProgressImageUrl();
      if (!currentImage) {
        const message = "Progress photo is missing. Please upload an image first.";
        setReviewSubmitError(message);
        logMessage(message, "alert");
        return;
      }

      const response = await api.requestReview({
        project_id: projectId,
        manual_user_percentage: getDisplayMilestone(),
        original_image: originalImage,
        current_image: currentImage,
        ai_verified_percent: getRawAiPercent(verificationResult, 0),
      });

      if (Number(response?.status) === 1) {
        showReviewSubmitted();
        logMessage(response?.message || "Review request submitted successfully", "alert");
        return;
      }

      const errorMessage = response?.message || "Failed to submit review request";
      setReviewSubmitError(errorMessage);
      logMessage(errorMessage, "alert");
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || error?.message || "Failed to submit review request";
      setReviewSubmitError(errorMessage);
      logMessage(errorMessage, "alert");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // Render Request Review Dialog first (it overlays on top of other states)
  if (showRequestReviewDialog) {
    return (
      <div className="fixed inset-0 z-[6000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4 font-sans select-none" onClick={(e) => {
        if (isSubmittingReview) return;
        if (e.target === e.currentTarget) setShowRequestReviewDialog(false);
      }}>
        <div className="w-full max-w-[600px] max-h-[90vh] bg-white rounded-[24px] shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 overflow-y-auto my-4">
          <div className="w-full bg-[#EFDEFF] py-4 px-6 text-center relative">
            {/* <button 
              type="button"
              onClick={() => setShowRequestReviewDialog(false)}
              className="absolute left-6 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-800 transition-colors text-xl font-medium w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 cursor-pointer bg-transparent border-0 outline-none focus:outline-none"
            >
              ✕
            </button> */}
            <p className="text-[#1A1A1A] text-2xl font-bold tracking-tight">
              Request Review
            </p>
          </div>

          <div className="p-6 md:px-8 py-6 flex flex-col gap-6">
            <p className="text-center text-sm md:text-base font-medium text-neutral-600 leading-relaxed px-2">
              If you believe your progress was evaluated incorrectly, you can request a manual review.
            </p>

            {/* Visual Comparison */}
            <div className="w-full">
              <h3 className="text-[#1A1A1A] text-base font-bold mb-3">Visual Comparison</h3>
              <div className="flex gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-neutral-500 mb-2">Project Image</p>
                  <div className="w-full h-[150px] rounded-[12px] overflow-hidden bg-neutral-100">
                    {projectDetail?.image?.[0] ? (
                      <img src={projectDetail.image[0]} alt="Project" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">No image</div>
                    )}
                  </div>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-neutral-500 mb-2">Your Photo</p>
                  <div className="w-full h-[150px] rounded-[12px] overflow-hidden bg-neutral-100">
                    {uploadedImagePreview ? (
                      <img src={uploadedImagePreview} alt="Your photo" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-400 text-sm">No image</div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* System Analysis */}
            <div className="w-full bg-[#F1F1F1] rounded-[12px] p-4">
              <div className="flex items-center justify-between gap-4 px-2">
                <div className="w-auto flex items-center gap-2">
                    <AIVerificationIcon />
                    <div>
                        <p className="text-[#1A1A1A] text-base font-bold mb-3 block">System Analysis</p>
                        <p className="text-sm font-medium text-[#323232] block">AI Claimed</p>
                    </div>
                </div>
                {(() => {
  const percent = getRawAiPercent(verificationResult, 0);

  return (
    /* OUTER CIRCLE: Uses a conic-gradient to fill the ring up to the exact percent */
    <div 
      className="w-16 h-16 rounded-full flex items-center justify-center shadow-sm"
      style={{
        background: `conic-gradient(#552F83 ${percent}%, #FFFFFF ${percent}% 100%)`
      }}
    >
      {/* INNER CIRCLE: Masks the center to create a clean progress ring */}
      <div className="w-[calc(100%-8px)] h-[calc(100%-8px)] rounded-full bg-[#F1F1F1] flex items-center justify-center">
        <span className="text-[#552F83] font-bold text-sm">
          {percent}%
        </span>
      </div>
    </div>
  );
})()}
              </div>
            </div>

            {/* Manual Claim */}
            <div className="w-full">
              <h3 className="text-[#1A1A1A] text-base font-bold mb-3">Manual Claim</h3>
              <p className="text-sm text-neutral-500 mb-4">
                Your selected milestone from the upload step.
              </p>
              
              <div className="w-full px-2 py-4 relative flex flex-col gap-4">
                <div className="relative w-full h-2 bg-neutral-100 rounded-full flex items-center">
                  <div 
                    className="absolute left-0 top-0 h-full bg-[#552F83] rounded-full transition-all duration-300"
                    style={{ width: `${getDisplayMilestone()}%` }}
                  />
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="25"
                    value={getDisplayMilestone()}
                    disabled
                    className="absolute inset-0 w-full h-full opacity-0 cursor-not-allowed z-10"
                  />
                  <div 
                    className="absolute top-[-6px] -translate-y-1/2 -translate-x-1/3 w-5 h-5 bg-[#552F83] border-4 border-white shadow-md rounded-full pointer-events-none transition-all duration-300 z-20"
                    style={{ left: `${getDisplayMilestone()}%` }}
                  />
                </div>
                <div className="w-full flex items-center justify-between text-xs md:text-sm font-semibold text-[#552F83] mt-1">
                  <span className="text-[#552F83]/70 font-bold">Milestones:</span>
                  <div className="flex-1 grid grid-cols-4 text-right pl-4">
                    {visibleMilestones.map((m) => (
                      <div
                        key={m}
                        className={`flex items-center justify-end gap-1.5 cursor-not-allowed ${
                          getDisplayMilestone() === m ? "text-[#552F83] font-bold" : "text-neutral-400 font-medium"
                        } ${m === 25 && "pr-8"} ${m === 50 && "pr-4"}`}
                      >
                        <span className={`w-2 h-2 rounded-full transition-colors ${getDisplayMilestone() === m ? "bg-[#552F83]" : "bg-neutral-300"}`} />
                        {m}%
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="w-full flex flex-col items-center gap-3 mt-2">
              {reviewSubmitError && (
                <p className="w-full text-sm text-red-600 font-medium text-center px-2">
                  {reviewSubmitError}
                </p>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRequestReviewSubmit();
                }}
                disabled={isSubmittingReview}
                className={`w-full sm:w-[280px] h-12 font-semibold text-base rounded-[12px] flex items-center justify-center gap-2 shadow-lg transition-all duration-200 outline-none border-0 ${
                  isSubmittingReview
                    ? "bg-[#552F83]/70 text-white cursor-not-allowed"
                    : "bg-[#552F83] hover:bg-[#432369] text-white shadow-[#552F83]/20 hover:shadow-xl cursor-pointer transform active:scale-[0.98]"
                }`}
              >
                {isSubmittingReview ? (
                  <>
                    <BiLoaderAlt className="size-5 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Submit for review"
                )}
              </button>
              <button
                type="button"
                onClick={() => setShowRequestReviewDialog(false)}
                disabled={isSubmittingReview}
                className={`text-neutral-500 hover:text-neutral-800 font-bold text-sm transition-colors py-1 bg-transparent border-0 outline-none shadow-none m-0 p-0 focus:outline-none focus:ring-0 ${
                  isSubmittingReview ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Review Pending Dialog
  if (showReviewPendingDialog) {
    return (
      <div className="fixed inset-0 z-[6000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4 font-sans select-none" onClick={(e) => {
        if (e.target === e.currentTarget) setShowReviewPendingDialog(false);
      }}>
        <div className="w-full max-w-[500px] bg-white rounded-[24px] shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 my-4">
          <div className="p-8 flex flex-col items-center gap-6 text-center">
            <div className="w-[160px] h-auto py-2 bg-[#F1ECFF] font-semibold text-sm rounded-[24px] ">
                <span className="bg-gradient-to-b from-[#512B80] to-[#A33B89] bg-clip-text text-transparent [background-image:linear-gradient(182.35deg,#512B80_-57.18%,#A33B89_91.87%)]">
                  Review Pending
                </span>
            </div>
            {/* <div className="w-20 h-20 bg-[#F1ECFF] rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="#552F83" className="w-10 h-10">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div> */}
            <PendingReviewIcon />
            <div>
              <h3 className="text-[#1A1A1A] text-xl font-bold mb-2">Sent for Review</h3>
              <p className="text-neutral-600 text-sm leading-relaxed">
               Your photo has been submitted for manual verification. We’ll notify you once it’s confirmed. Reviews usually complete within 24 hours.
              </p>
              {/* <p className="text-neutral-500 text-xs mt-2">
                Reviews usually complete within 24 hours.
              </p> */}
            </div>
            <button
              type="button"
              onClick={() => {
                setShowReviewPendingDialog(false);
                handleClose();
              }}
              className="w-full sm:w-[200px] h-12 bg-[#552F83] hover:bg-[#432369] text-white font-semibold text-base rounded-[12px] flex items-center justify-center shadow-lg shadow-[#552F83]/20 hover:shadow-xl transition-all duration-200 cursor-pointer transform active:scale-[0.98] outline-none border-0"
            >
              Ok
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render different states
  if (dialogState === "verifying") {
    return (
      <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4 font-sans select-none" onClick={handleBackdropClick}>
        <div className="w-full max-w-[600px] max-h-[90vh] bg-white rounded-[24px] shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 overflow-y-auto my-4">
          <div className="w-full bg-[#F3E8FF] py-4 px-6 text-center relative">
            <h2 className="text-[#1A1A1A] text-2xl font-bold tracking-tight">
              Claim Your Progress
            </h2>
          </div>
          <div className="p-6 md:px-8 py-6 flex flex-col gap-6">
          <p className="text-center text-sm md:text-base font-medium text-neutral-600 px-2 tracking-normal leading-relaxed">
            Upload a clear photo of your completed project. <br className="hidden sm:inline" />
            Select how much of the project you've completed. AI will verify this progress from your uploaded photo.
          </p>

          {uploadedImagePreview ? (
            <div className="w-full relative">
              <img src={uploadedImagePreview} alt="Uploaded" className="w-full h-[220px] object-cover rounded-[20px]" />
              <button
                type="button"
                disabled
                className="absolute bottom-3 right-3 bg-[#F1ECFF] px-4 py-2 rounded-[10px] text-[#552F83] font-semibold text-sm shadow-lg transition-colors cursor-not-allowed border border-[#552F83] flex items-center gap-1.5"
              >
                <BiEdit />
                Replace Image
              </button>
            </div>
          ) : (
            <label className="group relative w-full h-[220px] border-2 border-dashed border-[#552F83]/40 hover:border-[#552F83] bg-[#F1ECFF] rounded-[20px] flex flex-col items-center justify-center gap-3 cursor-not-allowed transition-all duration-200">
              <input 
                type="file" 
                accept="image/*" 
                className="hidden" 
                disabled
              />
              <UploadIcon />
              <div className="text-center flex flex-col gap-1">
                <span className="text-[#1A1A1A] text-base font-bold">
                  Upload Image
                </span>
                <span className="text-neutral-400 text-xs font-medium">
                  JPG or PNG upto 5mb
                </span>
              </div>
            </label>
          )}

          <div className="flex flex-col gap-1 mt-2">
            <span className="text-[#1A1A1A] text-lg font-bold">
              Select current completion
            </span>
            <span className="text-neutral-500 text-sm font-medium">
              Choose the milestone that matches your current progress.
            </span>
          </div>

          <div className="w-full px-2 py-4 relative flex flex-col gap-4">
            <div className="relative w-full h-2 bg-neutral-100 rounded-full flex items-center">
              <div 
                className="absolute left-0 top-0 h-full bg-[#552F83] rounded-full transition-all duration-300"
                style={{ width: `${getDisplayMilestone()}%` }}
              />
              <input
                type="range"
                min="0"
                max="100"
                step="25"
                value={getDisplayMilestone()}
                disabled
                className="absolute inset-0 w-full h-full opacity-0 cursor-not-allowed z-10"
              />
              <div 
                className="absolute top-[-6px] -translate-y-1/2 -translate-x-1/3 w-5 h-5 bg-[#552F83] border-4 border-white shadow-md rounded-full pointer-events-none transition-all duration-300 z-20"
                style={{ left: `${getDisplayMilestone()}%` }}
              />
            </div>
            <div className="w-full flex items-center justify-between text-xs md:text-sm font-semibold text-[#552F83] mt-1">
              <span className="text-[#552F83]/70 font-bold">Milestones:</span>
              <div className="flex-1 grid grid-cols-4 text-right pl-4">
                {visibleMilestones.map((m) => (
                  <div
                    key={m}
                    className={`flex items-center justify-end gap-1.5 cursor-not-allowed ${
                      getDisplayMilestone() === m ? "text-[#552F83] font-bold" : "text-neutral-400 font-medium"
                    } ${m === 25 && "pr-8"} ${m === 50 && "pr-4"}`}
                  >
                    <span className={`w-2 h-2 rounded-full transition-colors ${getDisplayMilestone() === m ? "bg-[#552F83]" : "bg-neutral-300"}`} />
                    {m}%
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center">
            <BiLoaderAlt className="text-[#552F83] size-16 stroke-3 animate-spin block leading-none " />
            <span className="text-[#323232] mt-3 text-lg font-bold leading-tight">Checking project details…</span>
            <span className="mt-3 text-neutral-500 text-sm font-medium leading-relaxed tracking-normal">AI is checking your project completion. This usually takes a few seconds.</span>
          </div>

          {/* <div className="w-full flex flex-col items-center gap-3 mt-2">
            <button
              type="button"
              onClick={handleSubmitForReview}
              disabled={!selectedFile || selectedMilestone === 0}
              className={`w-full sm:w-[280px] h-12 font-semibold text-base rounded-[12px] flex items-center justify-center gap-2 shadow-lg transition-all duration-200 cursor-pointer transform active:scale-[0.98] outline-none border-0 ${
                !selectedFile || selectedMilestone === 0
                  ? "bg-neutral-300 text-neutral-500 cursor-not-allowed"
                  : "bg-[#552F83] hover:bg-[#432369] text-white shadow-[#552F83]/20 hover:shadow-xl"
              }`}
            >
              <SparkleAIIcon className={`${(!selectedFile || selectedMilestone === 0) ? "text-neutral-500" : "text-white"} size-5`}/>
              Submit for AI Review
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="text-neutral-500 hover:text-neutral-800 font-bold text-sm transition-colors py-1 cursor-pointer bg-transparent border-0 outline-none shadow-none m-0 p-0 focus:outline-none focus:ring-0"
            >
              Cancel
            </button>
          </div> */}

          {/* Previous Requests Section */}
          {pendingReviewRequests && pendingReviewRequests.length > 0 && (
            <div className="w-full mt-4 pt-4 border-t border-neutral-200">
              <h3 className="text-[#1A1A1A] text-base font-bold mb-3">Previous Requests</h3>
              <div className="flex gap-3 overflow-x-auto pb-2 pt-3">
                {pendingReviewRequests.map((request) => (
                  <div key={request.review_request_id} className="w-full flex items-center gap-4 shadow-md px-4">
                    <div className="w-[60px] h-[60px] rounded-[12px] overflow-hidden mb-2 ">
                      <img 
                        src={request.original_image} 
                        alt="Previous request" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="w-[60px] h-[60px] rounded-[12px] overflow-hidden mb-2 ">
                      <img 
                        src={request.current_image} 
                        alt="Previous request" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex flex-col items-start justify-center">
                       <div className="text-sm font-medium text-[#111827]">
                        AI Claim: {request.ai_verified_percent}%
                       </div>
                       <div className="text-xs font-normal text-[#9CA3AF]">
                          Manual Claim: {request.manual_user_percentage}%
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    );
  }

  if (dialogState === "success") {
    return (
      <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4 font-sans select-none" onClick={handleBackdropClick}>
        <div className="w-full max-w-[600px] max-h-[90vh] bg-white rounded-[24px] shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 overflow-y-auto my-4">
          {/* <div className="w-full bg-[#D1FAE5] py-4 px-6 text-center relative">
            <h2 className="text-[#00934C] text-2xl font-bold tracking-tight">
              Progress Verified!
            </h2>
            <button 
              type="button"
              onClick={handleClose}
              className="absolute right-6 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-800 transition-colors text-xl font-medium w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 cursor-pointer bg-transparent border-0 outline-none focus:outline-none"
            >
              ✕
            </button>
          </div> */}
          <div className="w-full bg-[#EFDEFF] py-4 px-6 text-center relative">
          <p className="text-[#1A1A1A] text-2xl font-bold tracking-tight">
            Claim Your Progress
          </p>
          <button 
            type="button"
            onClick={handleClose}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-800 transition-colors text-xl font-medium w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 cursor-pointer bg-transparent border-0 outline-none focus:outline-none"
          >
            ✕
          </button>
        </div>
          <div className="p-6 md:px-8 py-6 flex flex-col gap-6">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-[#D1FAE5] rounded-full flex items-center justify-center">
                <div className="bg-[#00934C] w-14 h-14 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="#FFFFFF" className="w-10 h-10">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                </svg>
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-[#323232] text-xl font-bold">Progress Verified!</h3>
                <span className="text-sm font-normal text-[#323232]">
                    {verificationResult?.requested_milestone &&
                    verificationResult.requested_milestone !== verificationResult?.effective_milestone ? (
                      <>
                        You selected <span className="font-semibold text-[#552F83]">{verificationResult.requested_milestone}%</span>, AI estimated <span className="font-semibold text-[#552F83]">{verificationResult.ai_verified_completion_percent}%</span>, so your claimable milestone is <span className="font-semibold text-[#552F83]">{verificationResult?.effective_milestone}%</span>. <br/>
                      </>
                    ) : (
                      <>
                        You claimed <span className="font-semibold text-[#552F83]">{verificationResult?.effective_milestone}% completion</span>, and AI confirmed your project progress matches this milestone. <br/>
                      </>
                    )}
                    <span className="font-semibold text-[#552F83]">Great work on reaching this stage!</span>
                </span>
                {/* <div className="bg-[#D1FAE5] px-4 py-0.5 rounded-full flex items-center justify-center mt-4 max-w-[300px] mx-auto">
                <p className="text-[#00934C] text-lg font-semibold mt-1">+{verificationResult.claimable_xp} Points will be awarded</p>
                </div> */}
                <div className="bg-[#D1FAE5] px-8 py-0.5! rounded-full flex items-center justify-center mt-4 w-fit mx-auto">
                    <p className="text-[#00934C] text-sm font-semibold text-center">
                        +{verificationResult.claimable_xp} Points will be awarded
                    </p>
                </div>
              </div>
            </div>

            {uploadedImagePreview && (
              <div className="w-full">
                <img src={uploadedImagePreview} alt="Uploaded" className="w-full h-[200px] object-cover rounded-[16px]" />
              </div>
            )}

            <div className="flex flex-col gap-2">
              <span className="text-[#1A1A1A] text-lg font-bold">Your selected milestone</span>
              <span className="text-neutral-500 text-sm font-medium">
                AI verified milestone: {verificationResult.effective_milestone}%
              </span>
            </div>

            <div className="w-full px-2 py-4 relative flex flex-col gap-4">
              <div className="relative w-full h-2 bg-neutral-100 rounded-full flex items-center">
                <div 
                  className="absolute left-0 top-0 h-full bg-[#552F83] rounded-full transition-all duration-300"
                  style={{ width: `${getDisplayMilestone()}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="25"
                  value={getDisplayMilestone()}
                  disabled
                  className="absolute inset-0 w-full h-full opacity-0 cursor-not-allowed z-10"
                />
                <div 
                  className="absolute top-[-6px] -translate-y-1/2 -translate-x-1/3 w-5 h-5 bg-[#552F83] border-4 border-white shadow-md rounded-full pointer-events-none transition-all duration-300 z-20"
                  style={{ left: `${getDisplayMilestone()}%` }}
                />
              </div>
              <div className="w-full flex items-center justify-between text-xs md:text-sm font-semibold text-[#552F83] mt-1">
                <span className="text-[#552F83]/70 font-bold">Milestones:</span>
                <div className="flex-1 grid grid-cols-4 text-right pl-4">
                  {visibleMilestones.map((m) => (
                    <div key={m} className={`flex items-center justify-end gap-1.5 cursor-not-allowed ${m === 25 && "pr-8"} ${m === 50 && "pr-4"} ${
                      getDisplayMilestone() === m ? "text-[#552F83] font-bold" : "text-neutral-400 font-medium"
                    }`}>
                      <span className={`w-2 h-2 rounded-full ${getDisplayMilestone() === m ? "bg-[#552F83]" : "bg-neutral-300"}`} />
                      {m}%
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="w-full flex flex-col items-center gap-3 mt-2">
              {verificationResult.can_claim && (
                <button
                  type="button"
                  onClick={handleClaimPoints}
                  disabled={isClaiming}
                  className={`w-full sm:w-[280px] h-12 font-semibold text-base rounded-[12px] flex items-center justify-center gap-2 shadow-lg transition-all duration-200 outline-none border-0 ${
                    isClaiming
                      ? "bg-[#552F83]/70 text-white cursor-not-allowed"
                      : "bg-[#552F83] hover:bg-[#432369] text-white shadow-[#552F83]/20 hover:shadow-xl cursor-pointer transform active:scale-[0.98]"
                  }`}
                >
                  {isClaiming ? (
                    <>
                      <BiLoaderAlt className="size-5 animate-spin" />
                      Claiming...
                    </>
                  ) : (
                    "Claim Points"
                  )}
                </button>
              )}
              <button
                type="button"
                onClick={handleClose}
                disabled={isClaiming}
                className={`text-neutral-500 hover:text-neutral-800 font-bold text-sm transition-colors py-1 bg-transparent border-0 outline-none shadow-none m-0 p-0 focus:outline-none focus:ring-0 ${
                  isClaiming ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
                }`}
              >
                Close
              </button>
            </div>
          </div>
           <div className="bg-[#F1F1F1] w-full h-auto flex flex-col items-center justify-center px-4 py-4 pb-6">
                <span className="text-[#323232] text-center text-xs font-semibold pb-0!">
                    Not satisfied with the result?
                </span>
                <p className="font-normal text-[9px] text-[#000000] pb-0!">
                    If you believe your progress was evaluated incorrectly, you can request a manual review.
                </p>
                <button 
                    onClick={openRequestReviewDialog}
                    className="w-full sm:w-[280px] h-[50px] bg-[#F1ECFF] border border-[#552F83] rounded-[10px] font-semibold text-[#523A92] text-base mt-4"
                >
                    Request Review
                </button>
            </div>
        </div>
      </div>
    );
  }

  if (dialogState === "failure") {
    return (
      <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4 font-sans select-none" onClick={handleBackdropClick}>
        <div className="w-full max-w-[600px] max-h-[90vh] bg-white rounded-[24px] shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 overflow-y-auto my-4">
          {/* <div className="w-full bg-[#FEE2E2] py-4 px-6 text-center relative">
            <h2 className="text-[#DC2626] text-2xl font-bold tracking-tight">
              Progress Not Verified!
            </h2>
            <button 
              type="button"
              onClick={handleClose}
              className="absolute right-6 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-800 transition-colors text-xl font-medium w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 cursor-pointer bg-transparent border-0 outline-none focus:outline-none"
            >
              ✕
            </button>
          </div> */}
          <div className="w-full bg-[#EFDEFF] py-4 px-6 text-center relative">
            <p className="text-[#1A1A1A] text-2xl font-bold tracking-tight">
                Claim Your Progress
            </p>
            <button 
                type="button"
                onClick={handleClose}
                className="absolute right-6 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-800 transition-colors text-xl font-medium w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 cursor-pointer bg-transparent border-0 outline-none focus:outline-none"
            >
                ✕
            </button>
          </div>
          <div className="p-6 md:px-8 py-6 flex flex-col gap-6">
            <div className="flex flex-col items-center gap-4">
              <div className="w-20 h-20 bg-[#FEE2E2] rounded-full flex items-center justify-center">
                <MdError className="size-[46px] text-[#F1474C]"/>
              </div>
              <div className="text-center">
                <h3 className="text-[#323232] text-xl font-bold">
                  {verificationResult?.is_ai_error ? "AI Verification Issue" : "Progress Not Verified!"}
                </h3>
                <span className="text-[#323232] text-sm font-normal mt-2">
                    {verificationResult?.is_ai_error ? (
                      AI_VERIFICATION_ERROR_MESSAGE
                    ) : verificationResult?.message ? (
                      verificationResult.message
                    ) : (
                      <>
                        You selected <span className="font-semibold text-[#552F83]">{verificationResult?.requested_milestone || selectedMilestone}% completion</span>, but AI estimated <span className="font-semibold text-[#552F83]">{verificationResult?.ai_verified_completion_percent ?? 0}%</span>. At least 25% progress is required to claim a milestone.
                      </>
                    )}
                </span>
              </div>
            </div>

            {uploadedImagePreview && (
              <div className="w-full relative">
                <img src={uploadedImagePreview} alt="Uploaded" className="w-full h-[200px] object-cover rounded-[16px]" />
                <button
                  type="button"
                  onClick={handleUploadNewPhoto}
                  className="absolute bottom-3 right-3 bg-white px-4 py-2 rounded-[8px] text-[#552F83] font-semibold text-sm shadow-lg hover:bg-gray-50 transition-colors cursor-pointer"
                >
                  Replace Image
                </button>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <span className="text-[#1A1A1A] text-lg font-bold">Your selected milestone</span>
              <span className="text-neutral-500 text-sm font-medium">This matches what you chose in the upload step.</span>
            </div>

            <div className="w-full px-2 py-4 relative flex flex-col gap-4">
              <div className="relative w-full h-2 bg-neutral-100 rounded-full flex items-center">
                <div 
                  className="absolute left-0 top-0 h-full bg-[#552F83] rounded-full transition-all duration-300"
                  style={{ width: `${getDisplayMilestone()}%` }}
                />
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="25"
                  value={getDisplayMilestone()}
                  disabled
                  className="absolute inset-0 w-full h-full opacity-0 cursor-not-allowed z-10"
                />
                <div 
                  className="absolute top-[-6px] -translate-y-1/2 -translate-x-1/3 w-5 h-5 bg-[#552F83] border-4 border-white shadow-md rounded-full pointer-events-none transition-all duration-300 z-20"
                  style={{ left: `${getDisplayMilestone()}%` }}
                />
              </div>
              <div className="w-full flex items-center justify-between text-xs md:text-sm font-semibold text-[#552F83] mt-1">
                <span className="text-[#552F83]/70 font-bold">Milestones:</span>
                <div className="flex-1 grid grid-cols-4 text-right pl-4">
                  {visibleMilestones.map((m) => (
                    <div
                      key={m}
                      className={`flex items-center justify-end gap-1.5 cursor-not-allowed ${m === 25 && "pr-8"} ${m === 50 && "pr-4"} ${
                        getDisplayMilestone() === m ? "text-[#552F83] font-bold" : "text-neutral-400 font-medium"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full transition-colors ${getDisplayMilestone() === m ? "bg-[#552F83]" : "bg-neutral-300"}`} />
                      {m}%
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="w-full flex flex-col items-center gap-3 mt-2">
              <button
                type="button"
                onClick={handleUploadNewPhoto}
                className="w-full sm:w-[280px] h-12 bg-[#552F83] hover:bg-[#432369] text-white font-semibold text-base rounded-[12px] flex items-center justify-center gap-2 shadow-lg shadow-[#552F83]/20 hover:shadow-xl transition-all duration-200 cursor-pointer transform active:scale-[0.98] outline-none border-0"
              >
                Upload New Photo
              </button>
              <button
                type="button"
                onClick={handleClose}
                className="text-neutral-500 hover:text-neutral-800 font-bold text-sm transition-colors py-1 cursor-pointer bg-transparent border-0 outline-none shadow-none m-0 p-0 focus:outline-none focus:ring-0"
              >
                Cancel
              </button>
            </div>
          </div>
          {!verificationResult?.is_ai_error && (
          <div className="bg-[#F1F1F1] w-full h-auto flex flex-col items-center justify-center px-4 py-4 pb-6">
                <span className="text-[#323232] text-center text-xs font-semibold pb-0!">
                    Not satisfied with the result?
                </span>
                <p className="font-normal text-[9px] text-[#000000] pb-0!">
                    If you believe your progress was evaluated incorrectly, you can request a manual review.
                </p>
                <button 
                    onClick={openRequestReviewDialog}
                    className="w-full sm:w-[280px] h-[50px] bg-[#F1ECFF] border border-[#552F83] rounded-[10px] font-semibold text-[#523A92] text-base mt-4"
                >
                    Request Review
                </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default upload state
  return (
    <div className="fixed inset-0 z-[5000] flex items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4 font-sans select-none" onClick={handleBackdropClick}>
      <div className="w-full max-w-[600px] max-h-[90vh] bg-white rounded-[24px] shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 overflow-y-auto my-4">
        <div className="w-full bg-[#EFDEFF] py-4 px-6 text-center relative">
          <h2 className="text-[#1A1A1A] text-2xl font-bold tracking-tight">
            Claim Your Progress
          </h2>
          <button 
            type="button"
            onClick={handleClose}
            className="absolute right-6 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-neutral-800 transition-colors text-xl font-medium w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 cursor-pointer bg-transparent border-0 outline-none focus:outline-none"
          >
            ✕
          </button>
        </div>

        <div className="p-6 md:px-8 py-6 flex flex-col gap-6">
          {limitErrorMessage && (
            <p className="text-center text-sm md:text-base font-medium text-red-600 px-2 whitespace-pre-line">
              {limitErrorMessage}
            </p>
          )}
          <p className="text-center text-sm md:text-base font-medium text-neutral-600 px-2 tracking-normal leading-relaxed">
            Upload a clear photo of your completed project. <br className="hidden sm:inline" />
            Select how much of the project you've completed. AI will verify this progress from your uploaded photo.
          </p>

          {uploadedImagePreview ? (
            <div className={`w-full relative ${isLimitReached ? "opacity-60 pointer-events-none" : ""}`}>
              <img src={uploadedImagePreview} alt="Uploaded" className="w-full h-[220px] object-cover rounded-[20px]" />
              <button
                type="button"
                onClick={() => {
                  if (isLimitReached) {
                    showLimitToast(limitErrorMessage);
                    return;
                  }
                  setSelectedFile(null);
                  setUploadedImagePreview("");
                }}
                disabled={isLimitReached}
                className="absolute bottom-3 right-3 bg-[#F1ECFF] px-4 py-2 rounded-[10px] text-[#552F83] font-semibold text-sm shadow-lg hover:bg-gray-50 transition-colors cursor-pointer border border-[#552F83] flex items-center gap-1.5 disabled:cursor-not-allowed"
              >
                <BiEdit />
                Replace Image
              </button>
            </div>
          ) : (
            <div 
              className={`group relative w-full h-[220px] border-2 border-dashed border-[#552F83]/40 bg-[#F1ECFF] rounded-[20px] flex flex-col items-center justify-center gap-3 transition-all duration-200 ${
                isLimitReached
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:border-[#552F83] cursor-pointer"
              }`}
              onClick={() => {
                if (isLimitReached) {
                  showLimitToast(limitErrorMessage);
                }
              }}
            >
              <label className={`w-full h-full flex flex-col items-center justify-center gap-3 cursor-pointer ${isLimitReached ? "pointer-events-none" : ""}`}>
                <input 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  disabled={isLimitReached}
                  onChange={handleFileChange}
                />
                <UploadIcon />
                <div className="text-center flex flex-col gap-1">
                  <span className="text-[#1A1A1A] text-base font-bold">
                    Upload Image
                  </span>
                  <span className="text-neutral-400 text-xs font-medium">
                    JPG or PNG upto 5mb
                  </span>
                </div>
              </label>
            </div>
          )}

          <div className="flex flex-col gap-1 mt-2">
            <span className="text-[#1A1A1A] text-lg font-bold">
              Select current completion
            </span>
            <span className="text-neutral-500 text-sm font-medium">
              Choose the milestone that matches your current progress.
            </span>
          </div>

          <div className={`w-full px-2 py-4 relative flex flex-col gap-4 ${isLimitReached ? "opacity-60" : ""}`}>
            <div className="relative w-full h-2 bg-neutral-100 rounded-full flex items-center">
              <div 
                className="absolute left-0 top-0 h-full bg-[#552F83] rounded-full transition-all duration-300"
                style={{ width: `${selectedMilestone}%` }}
              />
              <input
                type="range"
                min="0"
                max="100"
                step="25"
                value={selectedMilestone}
                disabled={isLimitReached}
                onChange={(e) => {
                  if (isLimitReached) return;
                  setSelectedMilestone(Number(e.target.value));
                }}
                onClick={() => {
                  if (isLimitReached) {
                    showLimitToast(limitErrorMessage);
                  }
                }}
                className={`absolute inset-0 w-full h-full opacity-0 z-10 ${
                  isLimitReached ? "cursor-not-allowed" : "cursor-pointer"
                }`}
              />
              <div 
                className="absolute top-[-6px] -translate-y-1/2 -translate-x-1/3 w-5 h-5 bg-[#552F83] border-4 border-white shadow-md rounded-full pointer-events-none transition-all duration-300 z-20"
                style={{ left: `${selectedMilestone}%` }}
              />
            </div>
            <div className="w-full flex items-center justify-between text-xs md:text-sm font-semibold text-[#552F83] mt-1">
              <span className="text-[#552F83]/70 font-bold">Milestones:</span>
              <div className="flex-1 grid grid-cols-4 text-right pl-4">
                {visibleMilestones.map((m) => (
                  <button
                    key={m}
                    type="button"
                    disabled={isLimitReached}
                    onClick={() => {
                      if (isLimitReached) {
                        showLimitToast(limitErrorMessage);
                        return;
                      }
                      setSelectedMilestone(m);
                    }}
                    className={`flex items-center justify-end gap-1.5 transition-colors bg-transparent duration-200 border-0 outline-none m-0 p-0 shadow-none focus:outline-none focus:ring-0 active:outline-none ${
                      isLimitReached ? "cursor-not-allowed" : "cursor-pointer"
                    } ${
                      selectedMilestone === m ? "text-[#552F83] font-bold" : "text-neutral-400 font-medium"
                    } ${m === 25 && "pr-8"} ${m === 50 && "pr-4"}`}
                  >
                    <span className={`w-2 h-2 rounded-full transition-colors ${selectedMilestone === m ? "bg-[#552F83]" : "bg-neutral-300"}`} />
                    {m}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="w-full flex flex-col items-center gap-3 mt-2">
            <button
              type="button"
              onClick={() => {
                if (isLimitReached) {
                  showLimitToast(limitErrorMessage);
                  return;
                }
                handleSubmitForReview();
              }}
              disabled={isLimitReached || !selectedFile || selectedMilestone === 0 || isVerifying}
              className={`w-full sm:w-[280px] h-12 font-semibold text-base rounded-[12px] flex items-center justify-center gap-2 shadow-lg transition-all duration-200 outline-none border-0 ${
                isLimitReached || !selectedFile || selectedMilestone === 0 || isVerifying
                  ? "bg-neutral-300 text-neutral-500 cursor-not-allowed"
                  : "bg-[#552F83] hover:bg-[#432369] text-white shadow-[#552F83]/20 hover:shadow-xl cursor-pointer transform active:scale-[0.98]"
              }`}
            >
              {isVerifying ? (
                <>
                  <BiLoaderAlt className="size-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <SparkleAIIcon className={`${(isLimitReached || !selectedFile || selectedMilestone === 0) ? "text-neutral-500" : "text-white"} size-5`}/>
                  Submit for AI Review
                </>
              )}
            </button>
            <button
              type="button"
              onClick={handleClose}
              disabled={isVerifying}
              className={`text-neutral-500 hover:text-neutral-800 font-bold text-sm transition-colors py-1 bg-transparent border-0 outline-none shadow-none m-0 p-0 focus:outline-none focus:ring-0 ${
                isVerifying ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
              }`}
            >
              Cancel
            </button>
          </div>

          {/* Previous Requests Section */}
          {pendingReviewRequests && pendingReviewRequests.length > 0 && (
            <div className="w-full mt-4 pt-4 border-t border-neutral-200">
              <h3 className="text-[#1A1A1A] text-base font-bold mb-3">Previous Requests</h3>
              <div className="flex gap-3 overflow-x-auto pb-2 pt-3">
                {pendingReviewRequests.map((request) => (
                  <div key={request.review_request_id} className="w-full flex items-center gap-4 shadow-md px-4">
                    <div className="w-[60px] h-[60px] rounded-[12px] overflow-hidden mb-2 ">
                      <img 
                        src={request.original_image} 
                        alt="Previous request" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="w-[60px] h-[60px] rounded-[12px] overflow-hidden mb-2 ">
                      <img 
                        src={request.current_image} 
                        alt="Previous request" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex flex-col items-start justify-center">
                       <div className="text-sm font-medium text-[#111827]">
                        AI Claim: {request.ai_verified_percent}%
                       </div>
                       <div className="text-xs font-normal text-[#9CA3AF]">
                          Manual Claim: {request.manual_user_percentage}%
                       </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}